import axios from "axios";
import i18next from 'i18next';
import { getStoreSettings } from "./saveStoreSettings";
import { getStoreIdentity, restoreStoreSessionToLocalStorage } from "./storeSession";
import BACKEND_URL from "../config";

// ============================================================================
// REQUEST DEDUPLICATION
// Locks must never survive a background freeze — hung entries block Offers/Contact
// refetches and surface as false empty states after PWA resume.
// ============================================================================
const pendingRequests = new Map();
const DEDUPE_MAX_AGE_MS = 8000;

export function clearPendingRequests() {
    pendingRequests.clear();
}

const transport = axios.create({
    baseURL: BACKEND_URL,
    headers: {
        'Cache-Control': 'no-cache, no-store, must-revalidate',
        'Pragma': 'no-cache',
        'Expires': '0'
    }
});

// ============================================================================
// REQUEST INTERCEPTOR
// ============================================================================
transport.interceptors.request.use((config) => {
    const currentPath = window.location.pathname;
    const pathParts = currentPath.split('/').filter(Boolean);
    const slug = pathParts[0];

    // Re-assert this window's identity before headers (other PWAs share localStorage)
    if (slug && slug !== 'join-network') {
        restoreStoreSessionToLocalStorage(slug);
    }

    // Prefer slug-scoped settings so multi-store PWAs don't share another store's lang
    const settings = getStoreSettings(slug) || getStoreSettings();
    const isValidLang = settings?.lang && settings?.lang !== 'undefined';
    let deviceId = localStorage.getItem('deviceId');

    // ============================================================================
    // REQUEST DEDUPLICATION (short-lived only)
    // ============================================================================
    const requestKey = `${config.method}-${config.url}-${JSON.stringify(config.params || {})}-${slug}`;
    const existing = pendingRequests.get(requestKey);

    if (existing != null) {
        const age = Date.now() - existing;
        if (age < DEDUPE_MAX_AGE_MS) {
            const error = new Error('Duplicate request');
            error.__CANCEL__ = true;
            error.config = config;
            return Promise.reject(error);
        }
        pendingRequests.delete(requestKey);
    }

    pendingRequests.set(requestKey, Date.now());
    config.metadata = { requestKey };

    // ============================================================================
    // LANGUAGE HEADERS
    // ============================================================================
    if (currentPath === "/" || currentPath === "" || currentPath === "/join-network") {
        const lang = localStorage.getItem('i18nextLng') || 'ar';
        config.headers['lang'] = lang;
        config.headers['accept-language'] = lang;
    }
    else if (isValidLang) {
        const currentLanguageCode =
            localStorage.getItem(`store_i18nextLng-${slug}`) ||
            localStorage.getItem('i18nextLng') ||
            settings?.lang ||
            'ar';
        config.headers['lang'] = currentLanguageCode;
        config.headers['accept-language'] = currentLanguageCode;
    } else {
        delete config.headers.lang;
        delete config.headers['accept-language'];
    }

    // ============================================================================
    // STORE-ID HEADER — session-isolated per PWA window
    // ============================================================================
    const storeId = getStoreIdentity('store-id', slug) || localStorage.getItem(`store-id-${slug}`);

    if (storeId && storeId !== 'undefined') {
        config.headers['store-id'] = storeId;
    }

    // ============================================================================
    // DEVICE-ID HEADER
    // ============================================================================
    if (deviceId && deviceId !== 'undefined') {
        config.headers['device-id'] = deviceId;
    }

    // ============================================================================
    // SLUG HEADER
    // ============================================================================
    if (slug && slug !== 'join-network') {
        config.headers['X-Store-Slug'] = slug;
    }

    // ============================================================================
    // CACHE BUSTING
    // ============================================================================
    config.params = {
        ...config.params,
        _t: Date.now(),
        _s: slug || 'landing'
    };

    config.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
    config.headers['Pragma'] = 'no-cache';
    config.headers['Expires'] = '0';

    return config;
}, (error) => {
    return Promise.reject(error);
});

// ============================================================================
// RESPONSE INTERCEPTOR
// ============================================================================
transport.interceptors.response.use(
    (response) => {
        const requestKey = response.config?.metadata?.requestKey;
        if (requestKey) {
            pendingRequests.delete(requestKey);
        }

        if (response.headers) {
            response.headers['Cache-Control'] = 'no-cache, no-store, must-revalidate';
        }
        return response;
    },
    (error) => {
        const requestKey = error.config?.metadata?.requestKey;
        if (requestKey) {
            pendingRequests.delete(requestKey);
        }

        if (error.__CANCEL__) {
            return Promise.reject({ cancelled: true, silent: true });
        }

        if (error.response && error.response.status === 401) {
            console.error("Unauthorized! Redirecting to login...");
        }

        return Promise.reject(error);
    }
);

// ============================================================================
// CLEANUP + PWA RESUME
// ============================================================================
setInterval(() => {
    const now = Date.now();
    pendingRequests.forEach((timestamp, key) => {
        if (now - timestamp > DEDUPE_MAX_AGE_MS) {
            pendingRequests.delete(key);
        }
    });
}, 5000);

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'visible') {
            clearPendingRequests();
        }
    });
    window.addEventListener('pageshow', () => {
        clearPendingRequests();
    });
}

// ============================================================================
// LANGUAGE CHANGE LISTENER
// ============================================================================
i18next.on('languageChanged', (lng) => {
    localStorage.setItem('i18nextLng', lng);
    transport.defaults.headers['lang'] = lng;
    transport.defaults.headers['accept-language'] = lng;
});

export default transport;
